import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Clock, Camera } from 'lucide-react';
import { Avatar } from '@/components/Avatar';
import { StarRating } from '@/components/StarRating';
import { ListingCard } from '@/components/ListingCard';
import { ReviewsSection } from '@/components/ReviewsSection';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProfilePage = () => {
  const { id } = useParams();
  const { user: currentUser, checkAuth } = useAuth();
  const [profileUser, setProfileUser] = useState(null);
  const [listings, setListings] = useState([]);
  const [ratingSummary, setRatingSummary] = useState({ average_rating: 0, total_reviews: 0 });
  const [loading, setLoading] = useState(true);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  const isOwnProfile = currentUser && currentUser.id === id;

  useEffect(() => {
    fetchProfile();
  }, [id]);

  const fetchProfile = async () => {
    try {
      const { data: userData } = await axios.get(`${API}/users/${id}`);
      setProfileUser(userData);

      const { data: listingsData } = await axios.get(`${API}/users/${id}/listings`);
      setListings(listingsData);

      const { data: summaryData } = await axios.get(`${API}/reviews/seller/${id}/summary`);
      setRatingSummary(summaryData);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data: uploadResult } = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      await axios.put(
        `${API}/auth/me`,
        { profile_picture: uploadResult.url },
        { withCredentials: true }
      );

      toast.success('Profile picture updated!');
      await checkAuth();
      await fetchProfile();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to upload';
      toast.error(typeof detail === 'string' ? detail : 'Failed to upload');
    } finally {
      setUploadingAvatar(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9F6F0' }}>
        <p style={{ fontFamily: 'Manrope' }}>Loading...</p>
      </div>
    );
  }

  if (!profileUser) return null;

  return (
    <div style={{ backgroundColor: '#F9F6F0', minHeight: '100vh' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12 p-8 rounded-md border border-[#E5E0D8]" style={{ backgroundColor: '#FFFFFF' }}>
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="relative">
              <Avatar
                name={profileUser.name}
                imageUrl={profileUser.profile_picture}
                size={96}
              />
              {isOwnProfile && (
                <>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="absolute bottom-0 right-0 rounded-full p-2"
                    style={{ backgroundColor: '#D95A41', color: '#FFFFFF', border: '2px solid #FFFFFF' }}
                    data-testid="upload-avatar-btn"
                    title="Change profile picture"
                  >
                    <Camera size={14} strokeWidth={2} />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    data-testid="avatar-file-input"
                  />
                </>
              )}
            </div>
            <div className="flex-1">
              <h1
                className="text-4xl mb-2 tracking-tight"
                style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
                data-testid="profile-name"
              >
                {profileUser.name}
              </h1>
              {ratingSummary.total_reviews > 0 && (
                <div className="mb-3">
                  <StarRating rating={ratingSummary.average_rating} size={18} showValue totalReviews={ratingSummary.total_reviews} />
                </div>
              )}
              <div className="flex flex-wrap items-center gap-4 mb-4" style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#5C5C5C' }}>
                {profileUser.location && (
                  <div className="flex items-center gap-1">
                    <MapPin size={16} strokeWidth={1.5} />
                    <span>{profileUser.location}</span>
                  </div>
                )}
                {profileUser.response_time && (
                  <div className="flex items-center gap-1">
                    <Clock size={16} strokeWidth={1.5} />
                    <span>Responds {profileUser.response_time}</span>
                  </div>
                )}
              </div>
              {profileUser.bio && (
                <p style={{ fontFamily: 'Manrope', color: '#1A1A1A', lineHeight: '1.6' }} data-testid="profile-bio">
                  {profileUser.bio}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="mb-12">
          <h2
            className="text-3xl mb-6 tracking-tight"
            style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
            data-testid="listings-heading"
          >
            {profileUser.name}'s Listings
          </h2>
          {listings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((listing) => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 rounded-md border border-[#E5E0D8]" style={{ backgroundColor: '#FFFFFF' }}>
              <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>No listings yet</p>
            </div>
          )}
        </div>

        <ReviewsSection sellerId={id} />
      </div>
    </div>
  );
};
